/**
 * FieldApp: the mobile AppCore (reused unchanged: booths, sections, location, outbox, sync) plus
 * what the field web app adds: the field login (email or mobile, client "field"), the
 * per-assembly link, and the polling-day outbox (SOS, turnout).
 *
 * Plain TypeScript, no React, no browser globals: unit-tested in Node.
 */
import { AppCore } from "@core/app";
import type { ApiError } from "@core/api";
import { Emitter } from "@core/emitter";
import { roundAccuracy, type Fix } from "@core/location";
import { getOrCreateDeviceId, Session, type SecureKV } from "@core/session";
import type { SqlDb } from "@core/sql";
import { canEditBooth } from "@core/status";
import { LocalStore } from "@core/store";
import type { SyncRunResult, Timers } from "@core/sync";
import type { Booth } from "@core/types";
import { DUTIES, SOS_CATEGORIES, TURNOUT_SLOTS, screensFor, turnoutPct, type DemographicsData, type Duty, type FieldScreen, type SosCategory, type TurnoutSlot } from "@npac/rules";
import { FieldApi, type AssemblyInfo, type CampaignPart, type DdayBooth, type FieldUser, type Sos, type SosPhoto } from "./api";
import { chooseAssembly } from "./link";
import { PollingStore, PollingSync, type PollingItem, type SentRecord, type SosItem } from "./polling";
import { istParts } from "./time";

export interface FieldAppOptions {
  db: SqlDb;
  kv: SecureKV;
  /** random id generator (crypto.randomUUID in the browser) */
  makeId: () => string;
  baseUrl: () => string;
  fetch?: (input: string, init?: RequestInit) => Promise<Response>;
  now?: () => number;
  /** polling sender only (the reused mobile engine uses real timers) */
  timers?: Timers;
  random?: () => number;
}

export { classifyLoginError, type LoginFailure } from "@core/messages";

export interface CombinedSyncStatus {
  running: boolean;
  online: boolean | null;
  authRequired: boolean;
  lastRunAt: number | null;
  nextRetryAt: number | null;
  counts: { pending: number; failed: number; conflict: number; total: number };
}

export type SosResult =
  | { ok: true; id: number; clientId: string; sending: Promise<SyncRunResult> }
  | { ok: false; reason: "no_assembly" | "unknown_booth" | "not_assigned" | "bad_category" };

export type TurnoutResult =
  | {
      ok: true;
      id: number;
      date: string;
      pct: number | null;
      /** more votes than the total voters the phone knows: still sent (the total is often out of date) */
      overTotal: boolean;
      total: number | null;
      sending: Promise<SyncRunResult>;
    }
  | { ok: false; reason: "no_assembly" | "unknown_booth" | "not_assigned" | "bad_slot" | "bad_number" | "bad_our_votes" };

/** The worker's own SOS alerts as the war room sees them (GET /sos/mine), cached for offline. */
export interface MySos {
  alerts: Sos[];
  /** when the list was fetched (device time) */
  fetchedAt: number;
}

const K_ASSEMBLIES = "assemblies";
const kMySos = (aid: string) => `mySos:${aid}`;
const K_LAST_BOOTH = "lastBooth";
const K_DDAY_KEY = "ddayLink";
const K_DRIVE_KEY = "driveLink";
/** polling-day worker: total voters of their booths, as the D-Day login returned them */
const kDdayTotals = (aid: string) => `ddayTotals:${aid}`;
const PHOTO_MAX_BYTES = 1_500_000;

export class FieldApp {
  /** combined sync status changed (either outbox) */
  readonly statusChanges = new Emitter();
  readonly pollingSync: PollingSync;
  /** the link pointed at an assembly this login cannot open */
  linkMismatch: string | null = null;
  /** set by the UI: remember that a driver link was just opened, so the duty starts by itself */
  autoStartDrive: (() => void) | null = null;

  private constructor(
    readonly core: AppCore,
    readonly api: FieldApi,
    readonly polling: PollingStore,
    private readonly opts: FieldAppOptions,
    private readonly now: () => number
  ) {
    this.pollingSync = new PollingSync({
      store: polling,
      api,
      now,
      timers: opts.timers,
      random: opts.random,
      canSend: () => !!this.core.session.state.token,
      // every 401 ends the session the same way (and keeps the reason: expired / account_disabled)
      onUnauthenticated: (e) => void this.core.expireSession(e),
    });
    core.sync.changes.subscribe(() => this.statusChanges.emit());
    this.pollingSync.changes.subscribe(() => this.statusChanges.emit());
    core.changes.subscribe(() => this.statusChanges.emit());
  }

  static async create(o: FieldAppOptions): Promise<FieldApp> {
    const now = o.now ?? Date.now;
    const store = new LocalStore(o.db, now);
    await store.init();
    const session = new Session(o.kv);
    await session.load();
    const deviceId = await getOrCreateDeviceId(o.kv, o.makeId);
    const core = new AppCore({ store, session, deviceId, baseUrl: o.baseUrl, fetch: o.fetch, now });
    const api = new FieldApi({ baseUrl: o.baseUrl, getToken: () => session.state.token, fetch: o.fetch });
    const polling = new PollingStore(o.db, now);
    await polling.init();
    const app = new FieldApp(core, api, polling, o, now);
    await core.sync.refreshCounts();
    await app.pollingSync.refreshCounts();
    return app;
  }

  dispose() {
    this.core.sync.dispose();
    this.pollingSync.dispose();
  }

  /* ---------------- session ---------------- */

  get user(): FieldUser | null {
    return this.core.session.state.user;
  }
  /** Why the server ended the session (expired token, `account_disabled`); null after a login. */
  get authError(): ApiError | null {
    return this.core.authError;
  }
  get sessionExpired(): boolean {
    return this.core.authError != null;
  }
  get token(): string | null {
    return this.core.session.state.token;
  }
  get assemblyId(): string | null {
    return this.core.session.state.assemblyId;
  }

  /**
   * Log in with email or mobile, open the right assembly (the link's, if the login may open it),
   * download its booths into the local database, then resume sending anything queued.
   */
  async login(login: string, password: string, link: string | null, onPhase?: (p: "login" | "booths") => void) {
    onPhase?.("login");
    const r = await this.api.login(login, password); // { login, password, client: "field" }
    // an admin's login has a second step (N-PAC, 4 Nov 2026): the code on N-PAC's / the candidate's number
    if ("otpRequired" in r) return { otp: r, user: null, booths: null, linkMismatch: false };
    return this.finishLogin(r, link, onPhase);
  }

  /** the second step of an admin's login: the code → the token */
  async loginOtp(challengeId: string, code: string, link: string | null, onPhase?: (p: "login" | "booths") => void) {
    onPhase?.("login");
    const r = await this.api.otpVerify(challengeId, code);
    return this.finishLogin(r, link, onPhase);
  }

  private async finishLogin(r: { token: string; user: FieldUser }, link: string | null, onPhase?: (p: "login" | "booths") => void) {
    await this.core.session.save(r.token, r.user);
    this.core.authError = null;
    const choice = chooseAssembly(r.user, link);
    if (choice.assemblyId && choice.assemblyId !== this.core.session.state.assemblyId) await this.core.session.setAssembly(choice.assemblyId);
    this.linkMismatch = choice.linkMismatch ? link : null;
    let booths = null;
    if (this.assemblyId) {
      onPhase?.("booths");
      booths = await this.core.refreshBooths();
    }
    void this.cacheAssemblies();
    this.core.changes.emit();
    void this.core.sync.resumeAfterLogin();
    void this.pollingSync.resumeAfterLogin();
    return { otp: null, user: r.user, booths, linkMismatch: choice.linkMismatch };
  }

  /** Polling-day worker (D-Day login): only the polling screen, only their own booths. */
  get isWorker(): boolean {
    return this.user?.role === "worker";
  }

  /** A vehicle's driver (opened the driver link): only the duty / GPS screen. */
  get isDriver(): boolean {
    return this.user?.role === "driver";
  }

  /** What this login may open in the app: a surveyor's duties (none = all booth work); admins everything. */
  get screens(): FieldScreen[] {
    const u = this.user;
    if (!u || u.role === "worker" || u.role === "driver" || u.role === "viewer") return [];
    if (u.role === "surveyor") return screensFor((u.duties ?? null) as Duty[] | null);
    return screensFor(DUTIES);
  }
  can(screen: FieldScreen): boolean {
    return this.screens.includes(screen);
  }

  /** The assembly's booths for the polling-day link's booth picker. */
  async ddayBooths(assemblyId: string, key: string) {
    return this.api.ddayBooths(assemblyId.trim().toUpperCase(), key.trim());
  }

  /**
   * Polling-day access by the war room's link alone (its key; the booth only if the link names one).
   * No form: the app calls this as soon as the link opens. The reply's booths (all of the assembly)
   * become this phone's booth list, so the worker can type any booth on the SOS screen.
   */
  async ddayLogin(assemblyId: string, key: string, booth?: string | null, name?: string | null, mobile?: string | null) {
    const aid = assemblyId.trim().toUpperCase();
    const r = await this.api.ddayLogin(aid, key.trim(), booth ? booth.trim().toUpperCase() : null, name, mobile);
    await this.core.session.save(r.token, r.user);
    this.core.authError = null;
    await this.core.session.setAssembly(r.assembly.id);
    this.linkMismatch = null;
    await this.core.store.replaceBooths(r.assembly.id, r.booths.map(workerBooth));
    const totals: Record<string, number> = {};
    for (const b of r.booths) if (b.totalVoters != null && b.totalVoters > 0) totals[b.code] = b.totalVoters;
    await this.core.store.setKv(kDdayTotals(r.assembly.id), JSON.stringify(totals));
    await this.core.store.setKv(K_ASSEMBLIES, JSON.stringify([{ id: r.assembly.id, name: r.assembly.name, nameHi: r.assembly.nameHi ?? null }]));
    await this.core.store.setKv(K_DDAY_KEY, `${r.assembly.id}|${key.trim()}`);
    const first = r.picked ?? (r.booths.length === 1 ? r.booths[0].code : null);
    if (first) await this.rememberBooth(first);
    this.core.changes.emit();
    void this.pollingSync.resumeAfterLogin();
    void this.refreshMySos();
    return { user: r.user, booths: r.booths, assembly: r.assembly };
  }

  /** Driver link: signs the phone in as that vehicle's driver (no form). */
  async driveLogin(assemblyId: string, key: string) {
    const r = await this.api.driveLogin(assemblyId.trim().toUpperCase(), key.trim());
    await this.core.session.save(r.token, r.user);
    this.core.authError = null;
    await this.core.session.setAssembly(r.assembly.id);
    this.linkMismatch = null;
    await this.core.store.setKv(K_ASSEMBLIES, JSON.stringify([{ id: r.assembly.id, name: r.assembly.name, nameHi: r.assembly.nameHi ?? null }]));
    await this.core.store.setKv(K_DRIVE_KEY, `${r.assembly.id}|${key.trim()}`);
    // a link just opened: the duty starts by itself on the duty screen (not on later opens of the app)
    this.autoStartDrive?.();
    this.core.changes.emit();
    return r;
  }
  async driveLinkInUse(): Promise<string | null> {
    return this.isDriver ? this.core.store.getKv(K_DRIVE_KEY) : null;
  }

  /** The polling-day link this phone is signed in with ("HAR_01|key"), if it is a worker session. */
  async ddayLinkInUse(): Promise<string | null> {
    return this.isWorker ? this.core.store.getKv(K_DDAY_KEY) : null;
  }

  /**
   * Duties given or changed in the admin panel show up without logging in again (N-PAC, 31 Oct 2026: "जिसको
   * जो काम आवंटित किया जायेगा उसके आइडी पासवर्ड से खुलेगा"): the app asks the server who this login is when
   * it opens or comes back to the front, at most once a minute.
   */
  private meAt = 0;
  async refreshMe(force = false): Promise<void> {
    const u = this.user;
    if (!this.token || !u || u.role === "worker" || u.role === "driver") return;
    if (!force && Date.now() - this.meAt < 60_000) return;
    this.meAt = Date.now();
    try {
      const r = await this.api.me();
      if (r?.user && JSON.stringify(r.user) !== JSON.stringify(u)) {
        await this.core.session.setUser(r.user as FieldUser);
        this.core.changes.emit();
      }
      void this.cacheAssemblies();
    } catch (e) {
      if ((e as ApiError)?.status === 401) void this.core.expireSession(e as ApiError);
    }
  }

  /* ---------------- online-only work: past result, campaign ---------------- */

  /** Run an API call for the open assembly; a 401 ends the session like everywhere else. */
  async online<T>(fn: (api: FieldApi, aid: string) => Promise<T>): Promise<T> {
    const aid = this.assemblyId;
    if (!aid) throw new Error("No assembly selected");
    try {
      return await fn(this.api, aid);
    } catch (e) {
      if ((e as ApiError)?.status === 401) void this.core.expireSession(e as ApiError);
      throw e;
    }
  }
  campaign<T>(part: CampaignPart, day?: string) {
    return this.online((api, aid) => api.campaign<T>(aid, part, day));
  }
  campaignSave<T>(part: CampaignPart, body: object, id?: number) {
    return this.online((api, aid) => api.campaignSave<T>(aid, part, body, id));
  }

  async logout() {
    this.linkMismatch = null;
    await this.core.logout(); // the outboxes stay on the phone and are sent after the next login
  }

  /** The driver leaves the vehicle (N-PAC, 4 Nov 2026: "एक बार ऐड कर लिया था, कैंसल ही नहीं हो रहा"): forget the link, sign out. */
  async leaveDrive() {
    await this.core.store.setKv(K_DRIVE_KEY, "");
    await this.logout();
  }

  async chooseAssembly(id: string) {
    const r = await this.core.chooseAssembly(id);
    this.core.changes.emit();
    return r;
  }

  private async cacheAssemblies() {
    try {
      const list = await this.api.listAssemblies();
      await this.core.store.setKv(K_ASSEMBLIES, JSON.stringify(list.map((a) => ({ id: a.id, name: a.name, nameHi: a.nameHi ?? null, modules: a.modules ?? null }))));
      this.core.changes.emit();
    } catch {
      /* best effort */
    }
  }

  /** the module locks of the open assembly (null = all open), from the cached list */
  async lockedModules(): Promise<string[] | null> {
    const a = (await this.assemblies()).find((x) => x.id === this.assemblyId);
    return a?.modules ?? null;
  }

  async assemblies(): Promise<{ id: string; name: string; nameHi: string | null; modules?: string[] | null }[]> {
    const v = await this.core.store.getKv(K_ASSEMBLIES);
    try {
      return v ? JSON.parse(v) : [];
    } catch {
      return [];
    }
  }

  async listAssemblies(): Promise<AssemblyInfo[]> {
    const list = await this.api.listAssemblies();
    void this.core.store.setKv(K_ASSEMBLIES, JSON.stringify(list.map((a) => ({ id: a.id, name: a.name, nameHi: a.nameHi ?? null }))));
    return list;
  }

  /* ---------------- sync ---------------- */

  setOnline(online: boolean) {
    void this.core.sync.setOnline(online);
    void this.pollingSync.setOnline(online);
  }

  /** "Sync now": SOS / turnout and sections / locations, both right away. */
  syncNow(reason: "manual" | "foreground" | "startup" = "manual") {
    return Promise.all([this.pollingSync.trigger(reason), this.core.sync.trigger(reason)]);
  }

  status(): CombinedSyncStatus {
    const a = this.core.sync.status;
    const b = this.pollingSync.status;
    const retry = [a.nextRetryAt, b.nextRetryAt].filter((x): x is number => x != null);
    return {
      running: a.running || b.running,
      online: a.online ?? b.online,
      authRequired: a.authRequired || b.authRequired || !this.token,
      lastRunAt: Math.max(a.lastRunAt ?? 0, b.lastRunAt ?? 0) || null,
      nextRetryAt: retry.length ? Math.min(...retry) : null,
      counts: {
        pending: a.counts.pending + b.counts.pending,
        failed: a.counts.failed + b.counts.failed,
        conflict: a.counts.conflict,
        total: a.counts.total + b.counts.total,
      },
    };
  }

  /* ---------------- booths ---------------- */

  async booth(code: string): Promise<Booth | null> {
    return this.assemblyId ? this.core.store.getBooth(this.assemblyId, code) : null;
  }

  canEdit(booth: Pick<Booth, "no">): boolean {
    return canEditBooth(this.user, booth);
  }

  async lastBooth(): Promise<string | null> {
    return this.core.store.getKv(K_LAST_BOOTH);
  }
  async rememberBooth(code: string) {
    await this.core.store.setKv(K_LAST_BOOTH, code);
  }

  /** Total voters for a booth: the unsent local demographics if any, else the server copy. */
  async totalVoters(code: string): Promise<number | null> {
    const aid = this.assemblyId;
    if (!aid) return null;
    const local = await this.core.store.findSectionItem(aid, code, "demographics");
    const d = (local?.payload.data ?? (await this.core.store.getServerSection(aid, code, "demographics"))?.data) as DemographicsData | undefined;
    if (d && d.totalVoters > 0) return d.totalVoters;
    if (!this.isWorker) return null;
    try {
      const v = await this.core.store.getKv(kDdayTotals(aid));
      const n = v ? (JSON.parse(v) as Record<string, number>)[code] : undefined;
      return typeof n === "number" && n > 0 ? n : null;
    } catch {
      return null;
    }
  }

  private async checkBooth(code: string): Promise<{ ok: true; aid: string; booth: Booth } | { ok: false; reason: "no_assembly" | "unknown_booth" | "not_assigned" }> {
    const aid = this.assemblyId;
    if (!aid) return { ok: false, reason: "no_assembly" };
    const booth = await this.core.store.getBooth(aid, code);
    if (!booth) return { ok: false, reason: "unknown_booth" };
    if (!this.canEdit(booth)) return { ok: false, reason: "not_assigned" };
    return { ok: true, aid, booth };
  }

  /* ---------------- polling day ---------------- */

  /**
   * Queue an SOS and start sending it immediately (even if the phone reports offline). The
   * clientId is made here, once; every retry reuses it. The GPS fix is sent as it is: a point
   * outside India never costs the alert (the API saves it without the point and says
   * `location_ignored`). Only readings that are not numbers at all are left out.
   */
  async raiseSos(input: { boothCode: string; category: SosCategory; note?: string; fix?: Fix | null; photo?: SosPhoto | null }): Promise<SosResult> {
    const c = await this.checkBooth(input.boothCode);
    if (!c.ok) return c;
    if (!(SOS_CATEGORIES as readonly string[]).includes(input.category)) return { ok: false, reason: "bad_category" };
    const f = input.fix;
    const fix = f && Number.isFinite(f.lat) && Number.isFinite(f.lng) && Math.abs(f.lat) <= 90 && Math.abs(f.lng) <= 180 ? f : null;
    const clientId = this.opts.makeId();
    const id = await this.polling.queueSos({
      assemblyId: c.aid,
      boothCode: c.booth.code,
      clientId,
      payload: {
        category: input.category,
        note: input.note?.trim().slice(0, 500) || null,
        lat: fix ? fix.lat : null,
        lng: fix ? fix.lng : null,
        accuracyM: fix && fix.accuracyM != null && Number.isFinite(fix.accuracyM) ? Math.min(roundAccuracy(fix.accuracyM)!, 100000) : null,
        raisedAt: new Date(this.now()).toISOString(),
        photo: validPhoto(input.photo),
      },
    });
    await this.rememberBooth(c.booth.code);
    const sending = this.pollingSync.trigger("manual");
    // once it is through, fetch the war room's view of it
    void sending.then((r) => (r.sent > 0 ? this.refreshMySos() : null)).catch(() => undefined);
    return { ok: true, id, clientId, sending };
  }

  /** Where one SOS stands: still queued (pending / failed) or sent (with the server's alert). */
  async sosState(clientId: string): Promise<{ state: "pending" | "failed"; item: SosItem } | { state: "sent"; record: SentRecord } | null> {
    const aid = this.assemblyId;
    if (!aid) return null;
    const items = await this.polling.list();
    const item = items.find((i): i is SosItem => i.kind === "sos" && i.key === clientId);
    if (item) return { state: item.status, item };
    const record = await this.polling.findSent("sos", aid, clientId);
    return record ? { state: "sent", record } : null;
  }

  /**
   * Queue a turnout report for a slot of today's (India time) poll and send it right away.
   * More votes than the total the phone knows is only a warning: the API saves it (without a %),
   * because the total is usually what is out of date.
   */
  async reportTurnout(input: { boothCode: string; slot: TurnoutSlot; votesPolled: number; ourVotes?: number | null }): Promise<TurnoutResult> {
    const c = await this.checkBooth(input.boothCode);
    if (!c.ok) return c;
    if (!(TURNOUT_SLOTS as readonly string[]).includes(input.slot)) return { ok: false, reason: "bad_slot" };
    const v = input.votesPolled;
    if (!Number.isInteger(v) || v < 0 || v > 100000) return { ok: false, reason: "bad_number" };
    const ours = input.ourVotes ?? null;
    if (ours != null && (!Number.isInteger(ours) || ours < 0 || ours > v)) return { ok: false, reason: "bad_our_votes" };
    const total = await this.totalVoters(c.booth.code);
    const overTotal = total != null && v > total;
    const date = istParts(this.now()).date;
    const id = await this.polling.queueTurnout({ assemblyId: c.aid, boothCode: c.booth.code, payload: { slot: input.slot, date, votesPolled: v, ourVotes: ours } });
    await this.rememberBooth(c.booth.code);
    return { ok: true, id, date, pct: overTotal ? null : turnoutPct(v, total), overTotal, total, sending: this.pollingSync.trigger("manual") };
  }

  /** Today's turnout reports for a booth: sent ones (with the server's % / warning) and queued ones, by slot. */
  async turnoutToday(code: string): Promise<Partial<Record<TurnoutSlot, TurnoutState>>> {
    const aid = this.assemblyId;
    if (!aid) return {};
    const date = istParts(this.now()).date;
    const out: Partial<Record<TurnoutSlot, TurnoutState>> = {};
    const sent = await this.polling.sent(aid, "turnout", 200);
    for (const r of sent.slice().reverse()) {
      if (r.boothCode !== code || !r.key.startsWith(`${date}|`)) continue;
      const t = r.result.turnout;
      const p = r.payload as { slot: TurnoutSlot; votesPolled: number; ourVotes?: number | null };
      out[p.slot] = { votesPolled: p.votesPolled, ourVotes: p.ourVotes ?? null, pct: t?.pct ?? null, state: "sent", warning: t?.warning };
    }
    for (const i of await this.polling.list()) {
      if (i.kind !== "turnout" || i.assemblyId !== aid || i.boothCode !== code || i.payload.date !== date) continue;
      out[i.payload.slot] = { votesPolled: i.payload.votesPolled, ourVotes: i.payload.ourVotes ?? null, pct: null, state: i.status, error: i.lastError ?? undefined };
    }
    return out;
  }

  async pollingItems(): Promise<PollingItem[]> {
    return this.polling.list();
  }

  /* ---------------- my SOS (the war room's view) ---------------- */

  /** Last fetched list of the worker's own alerts (kept on the phone, so it shows offline too). */
  async mySos(): Promise<MySos | null> {
    const aid = this.assemblyId;
    if (!aid) return null;
    const v = await this.core.store.getKv(kMySos(aid));
    try {
      return v ? (JSON.parse(v) as MySos) : null;
    } catch {
      return null;
    }
  }

  /**
   * Fetch GET /sos/mine and keep it. Quietly does nothing offline or when logged out; the screens
   * call this when they open, every 30 s while visible, when the network comes back and after an
   * SOS went through.
   */
  async refreshMySos(): Promise<MySos | null> {
    const aid = this.assemblyId;
    if (!aid || !this.token) return null;
    try {
      const r = await this.api.mySos(aid);
      const v: MySos = { alerts: r.alerts, fetchedAt: this.now() };
      await this.core.store.setKv(kMySos(aid), JSON.stringify(v));
      return v;
    } catch (e) {
      if ((e as ApiError)?.status === 401) void this.core.expireSession(e as ApiError);
      return null;
    }
  }
}

export interface TurnoutState {
  votesPolled: number;
  ourVotes?: number | null;
  pct: number | null;
  state: "sent" | "pending" | "failed";
  /** from the server: "no_total_voters" / "more_than_total_voters" (saved, but no %) */
  warning?: string;
  error?: { message: string; messageHi?: string };
}

/** A worker's booth as a (minimal) local booth record: enough for the polling screen. */
function workerBooth(b: DdayBooth): Booth {
  const m = /^(\d+)(.*)$/.exec(b.code);
  return {
    id: 0,
    no: b.no,
    aux: m ? m[2] : "",
    code: b.code,
    name: b.name,
    mandal: null,
    panchayat: b.panchayat ?? null,
    village: null,
    isNew: false,
    location: null,
    finalized: false,
    sections: {},
  };
}

const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

/** Keep a photo only if it is one of the accepted types and small enough for the API (1.5 MB). */
function validPhoto(p: SosPhoto | null | undefined): SosPhoto | null {
  if (!p || !PHOTO_TYPES.includes(p.type) || typeof p.data !== "string" || !p.data) return null;
  const bytes = Math.floor((p.data.length * 3) / 4);
  return bytes <= PHOTO_MAX_BYTES ? { type: p.type, data: p.data } : null;
}
